import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { IState } from 'store';
import { splitArray } from 'utils/splitArray';
import { objectIsEmpty } from 'utils/objectEmpty';
import { IUserProps } from 'store/modules/profile';
import { IReviewProps, loadProductReviews } from 'store/modules/product';

import Button from 'components/common/Button';
import { Pagination } from 'components/common/Pagination';
import { SimpleSelect } from 'components/common/Select/SimpleSelect';

import { Rating } from '../Rating';
import { ProductRatingItem } from './ProductRatingItem';
import { ModalRatingProduct } from './ModalRatingProduct';

import * as S from './styles';

export function ProductRatingReviews({ product, rating }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { slug, ModalRating } = router.query;

  const reviews = useSelector<IState, IReviewProps[]>(
    state => state.product.reviews,
  );
  const user = useSelector<IState, IUserProps>(state => state.profile.user);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [quantityItems, setQuantityItems] = useState(5);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [paginationReviews, setPaginationReviews] = useState<IReviewProps[]>();
  const [showModalRatingProduct, setShowModalRatingProduct] = useState(() => {
    if (ModalRating && !objectIsEmpty(user)) {
      if (ModalRating) {
        delete router.query.ModalRating;
        router.replace(router);
      }
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (!objectIsEmpty(product)) {
      dispatch(
        loadProductReviews({
          productId: product.id,
          page,
          quantity: quantityItems,
        }),
      );
    }
  }, [dispatch, product, page, quantityItems]);

  useEffect(() => {
    const newGroupReviews = splitArray(reviews, quantityItems);
    const hasPrevious = page > 1;
    const hasNext = page < newGroupReviews.length;

    setHasNextPage(hasNext);
    setHasPreviousPage(hasPrevious);
    setTotalPages(newGroupReviews.length);
    setPaginationReviews(newGroupReviews[page - 1]);
  }, [reviews, quantityItems, page]);

  const handleToggleRating = useCallback(() => {
    if (!user || objectIsEmpty(user)) {
      router.push(`/identificacao?ReturnUrl=/${slug}?ModalRating=true`);
    } else {
      setShowModalRatingProduct(true);
    }
  }, [router, user, slug]);

  return (
    <S.Container className="product-rating-reviews" id="reviews">
      {rating && rating.total > 0 ? (
        <div className="product-rating-reviews-content">
          <div className="product-rating-group">
            <span className="rating-product">{rating.media}</span>
            <span className="rating-max">/5</span>
          </div>

          <span>Nota do Produto</span>

          <Rating nota={rating.media} />

          <span>
            Baseado em {rating.total}
            {rating.total > 1 ? ' avaliações' : ' avaliação'}{' '}
          </span>

          <div className="product-reviews">
            <div className="product-reviews-options">
              <span>Avaliações mais recentes</span>

              <SimpleSelect
                name="filterBy"
                options={[
                  {
                    label: 'mais recentes',
                    value: 'mais_recentes',
                  },
                  {
                    label: 'mais antigos',
                    value: 'mais_antigos',
                  },
                  {
                    label: 'maior avaliação',
                    value: 'maior_avaliacao',
                  },
                  {
                    label: 'menor avaliação',
                    value: 'menor_avaliacao',
                  },
                ]}
              />
            </div>

            <div className="rating-list">
              {paginationReviews?.map((item, index) => (
                <ProductRatingItem key={index} item={item} />
              ))}
            </div>

            <Pagination
              onPageChange={setPage}
              pageIndex={page}
              totalPages={totalPages}
              totalCount={reviews.length}
              resultsPerPage={quantityItems}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
            />
          </div>
        </div>
      ) : (
        <div className="product-rating-without-reviews">
          <Rating nota={0} />

          <span>Este produto ainda não possui avaliações</span>

          <span>Seja o primeiro a avaliar:</span>

          <Button onClick={handleToggleRating}>Quero Avaliar</Button>
        </div>
      )}

      {showModalRatingProduct && (
        <ModalRatingProduct
          setShowModalRatingProduct={setShowModalRatingProduct}
          product={product}
        />
      )}
    </S.Container>
  );
}
